import { createPromiseClient } from "@connectrpc/connect";
import { transportMeaningOf } from "@/shared/transportMeaning";
import { migrateProjectData } from '@/app/persistence/projectMigration';
import { formatElapsed } from '@/shared/formatElapsed';
import type { PromiseClient } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import { ProjectManager } from "@/stubs/pm/project_manager_connect";
import {
  GetProjectRequest,
  GetSimulationHistoryRequest,
  CreateProjectRequest,
  CreateWorkspaceRequest,
  DeleteProjectRequest,
  DeleteSimulationHistoryRequest,
  DeleteWorkspaceRequest,
  ListProjectsRequest,
  ListSimulationHistoriesByProjectRequest,
  ListSimulationHistoriesByUserRequest,
  ListWorkspacesRequest,
  SetProjectWorkspaceRequest,
  type Project as PmProject, ProjectSummary as PmProjectSummary,
  type SimulationHistory as PmSimulationHistory,
  type Workspace as PmWorkspace,
  UpdateProjectRequest,
  UpdateWorkspaceRequest,
} from "@/stubs/pm/project_manager_pb";
import { BFF_URL, createAuthInterceptor } from "@/app/services/connectCommon";
import type { ParticipantSpec, ParticipantVerdict, Project, ProjectData, ProjectSummary, SimulationEntry, Workspace } from "@/types/project";
import { entryStatusFromServer } from "@/types/project";
import { formatTime } from '@/shared/dateTime';
import { LIST_PAGE_SIZE, readAllPages } from '@/shared/async/readAllPages';
import { soleParticipant } from '@/domain/cosim/coupling';

const transport = createConnectTransport({
  baseUrl: `${BFF_URL}/api`,
  interceptors: [createAuthInterceptor()],
});

const projectManagerClient: PromiseClient<typeof ProjectManager> = createPromiseClient(
  ProjectManager,
  transport
);

function toIsoTimestamp(timestamp: { seconds?: bigint | number | string; nanos?: number } | undefined): string {
  if (!timestamp) {
    return new Date().toISOString();
  }

  let seconds = 0;
  if (typeof timestamp.seconds === "bigint") {
    seconds = Number(timestamp.seconds);
  } else if (typeof timestamp.seconds === "string") {
    seconds = Number(timestamp.seconds);
  } else if (typeof timestamp.seconds === "number") {
    seconds = timestamp.seconds;
  }

  const nanos = typeof timestamp.nanos === "number" ? timestamp.nanos : 0;
  const millis = (seconds * 1000) + Math.floor(nanos / 1_000_000);
  return new Date(millis).toISOString();
}

function parseProjectData(dataJson: string): ProjectData | null {
  if (!dataJson) {
    return null;
  }

  try {
    // 서버에서 들어오는 유일한 자리 — 은퇴한 scope id 는 여기서 공식 id 로 바뀐다(다음 저장 때 기록)
    return migrateProjectData(JSON.parse(dataJson) as ProjectData);
  } catch (error) {
    console.warn("[projectManagerService] Failed to parse project data_json", error);
    return null;
  }
}

/** PM 응답 → Project. 저장된 scope 의 은퇴 id 는 여기서 공식 id 가 된다(parseProjectData) */
export function toProject(pmProject: PmProject): Project {
  return {
    id: pmProject.id,
    user_id: pmProject.userId,
    name: pmProject.name,
    description: pmProject.description || null,
    data: parseProjectData(pmProject.dataJson),
    created_at: toIsoTimestamp(pmProject.createdAt),
    updated_at: toIsoTimestamp(pmProject.updatedAt),
    workspace_id: pmProject.workspaceId || '',
  };
}

function toProjectSummary(pm: PmProjectSummary): ProjectSummary {
  return {
    id: pm.id,
    user_id: pm.userId,
    name: pm.name,
    description: pm.description || null,
    created_at: toIsoTimestamp(pm.createdAt),
    updated_at: toIsoTimestamp(pm.updatedAt),
    workspace_id: pm.workspaceId || '',
    model_count: pm.modelCount,
    node_count: pm.nodeCount,
    simulation_count: pm.simulationCount,
    analysis_codes: [...pm.analysisCodes],
  };
}

function toWorkspace(pmWorkspace: PmWorkspace): Workspace {
  return {
    id: pmWorkspace.id,
    user_id: pmWorkspace.userId,
    name: pmWorkspace.name,
    description: pmWorkspace.description || '',
    created_at: toIsoTimestamp(pmWorkspace.createdAt),
    updated_at: toIsoTimestamp(pmWorkspace.updatedAt),
    is_default: pmWorkspace.isDefault,
  };
}

/** proto 판이 앞서가는 필드까지 담는 판독 형태 — 값 검증은 각 필드에서 한다 */
type HistoryLike = Partial<PmSimulationHistory> & {
  simulationId?: string;
  startTime?: { seconds?: number | string; nanos?: number };
  endTime?: { seconds?: number | string; nanos?: number };
  taskIds?: unknown; modelIds?: unknown; participants?: unknown; participantSpecs?: unknown;
  runMode?: string; title?: string; status?: string; projectId?: string;
};

export function toSimulationEntry(raw: PmSimulationHistory): SimulationEntry {
  const sh = raw as HistoryLike;
  // 실행 id 는 simulation_id 하나다(proto 필드 1). 옛 taskId 대비책은 그 필드가 언제나 문자열로 와서 닿지 않았다.
  const simulationId = String(sh.simulationId ?? '').trim();
  // id 없는 행(빈·공백뿐)은 열 수도 지울 수도 없다 — id 를 지어내지 않고 던진다(목록 조회는 그 행만 버리고 알린다)
  if (!simulationId) throw new Error('simulation history row has no simulationId');
  const startIso = toIsoTimestamp(sh.startTime);
  const endIso =
    sh.endTime && (Number(sh.endTime.seconds) > 0 || sh.endTime.nanos > 0)
      ? toIsoTimestamp(sh.endTime)
      : '';

  const timeRange = endIso
    ? `${formatTime(startIso)} ~ ${formatTime(endIso)}`
    : formatTime(startIso);

  const taskIds: string[] = Array.isArray(sh.taskIds)
    ? sh.taskIds.filter((t: unknown) => typeof t === 'string' && t)
    : [];

  const modelIds: string[] = Array.isArray(sh.modelIds)
    ? sh.modelIds.filter((m: unknown) => typeof m === 'string' && m)
    : [];
  // 참가자별 판정(저널 v3) — 필드가 없거나 비었으면 빈 목록(구 이력): 소비자가 실행 판정으로 대신한다
  const participants: ParticipantVerdict[] = (Array.isArray(sh.participants) ? (sh.participants as unknown[]) : [])
    .filter((p): p is { taskId: string; finalState: string; detail?: unknown } =>
      !!p && typeof p === 'object' && typeof (p as { taskId?: unknown }).taskId === 'string' && typeof (p as { finalState?: unknown }).finalState === 'string')
    // 사유(detail)는 서버가 실어 보낸 원문 그대로 — 비었거나 문자열이 아니면 필드를 두지 않는다(모르는 것은 없는 것으로)
    .map((p) => ({ taskId: p.taskId, finalState: p.finalState, ...(typeof p.detail === 'string' && p.detail ? { detail: p.detail } : {}) }));
  // 참가자 신원(시작 시점 스냅샷) — 서버가 모르는 값은 빈 문자열로 오므로 필드를 두지 않는다(모르는 것은 없는 것으로)
  const participantSpecs: ParticipantSpec[] = (Array.isArray(sh.participantSpecs) ? (sh.participantSpecs as unknown[]) : [])
    .filter((p): p is { taskId: string; modelId?: unknown; modelName?: unknown; kind?: unknown } =>
      !!p && typeof p === 'object' && typeof (p as { taskId?: unknown }).taskId === 'string' && !!(p as { taskId: string }).taskId)
    .map((p) => ({
      taskId: p.taskId,
      ...(typeof p.modelId === 'string' && p.modelId ? { modelId: p.modelId } : {}),
      ...(typeof p.modelName === 'string' && p.modelName ? { modelName: p.modelName } : {}),
      ...(typeof p.kind === 'string' && p.kind ? { kind: p.kind } : {}),
    }));
  const runModeRaw = String(sh.runMode || '');
  const runMode: 'single' | 'cosim' | '' =
    runModeRaw === 'single' || runModeRaw === 'cosim' ? runModeRaw : '';

  return {
    id: simulationId,
    name: String(sh.title ?? ''), // 비었으면 빈 그대로 — 이름을 지어내지 않는다(제목 규칙은 시작의 공통 길 runStart 가 지킨다)
    timestamp: startIso,
    // 소요 = 종료 − 시작(BFF 종결 확정이 end_time 을 쓴다). 실행 중은 빈 문자열 — 자리를 대시로 채우지 않는다.
    // cpu_time 열은 생산자가 없어 읽지 않는다.
    duration: endIso ? formatElapsed((new Date(endIso).getTime() - new Date(startIso).getTime()) / 1000) : '',
    timeRange,
    status: entryStatusFromServer(sh.status ?? ''),
    rawStatus: String(sh.status || '').trim().toLowerCase(),
    // 참가 모델이 하나뿐이고 cosim 이 명시되지 않았으면 단독으로 추정해 귀속 —
    // run_mode 누락/비정상(부분 배포·백필 과도기)에도 modelId 기반 필터가 동작
    modelId: runMode !== 'cosim' ? soleParticipant(modelIds) ?? '' : '',
    ...(taskIds.length > 0 ? { taskIds } : {}),
    modelIds,
    runMode,
    ...(participants.length > 0 ? { participants } : {}),
    ...(participantSpecs.length > 0 ? { participantSpecs } : {}),
    ...(endIso ? { endedAt: endIso } : {}),
    projectId: String(sh.projectId || ''),
    projectName: String(sh.projectName ?? '') || undefined,
    // 고른 시나리오(시작 시점 스냅샷) — 서버가 빈 값이면 시나리오 없는 실행(칸을 두지 않는다)
    ...(sh.scenarioId ? { scenarioId: String(sh.scenarioId), scenarioName: String(sh.scenarioName ?? '') } : {}),
  };
}

export async function createProject(params: {
  id: string;
  userId: string;
  name: string;
  description?: string | null;
  data?: ProjectData | null;
  workspaceId?: string;
}): Promise<Project> {
  const request = new CreateProjectRequest({
    id: params.id,
    userId: params.userId,
    name: params.name,
    description: params.description ?? "",
    dataJson: JSON.stringify(params.data ?? {}),
    workspaceId: params.workspaceId ?? "",
  });

  const response = await projectManagerClient.createProject(request);
  if (!response.project) {
    throw new Error("createProject response has no project");
  }

  return toProject(response.project);
}

export async function updateProject(params: {
  id: string;
  name: string;
  description?: string | null;
  data?: ProjectData | null;
}): Promise<Project> {
  const request = new UpdateProjectRequest({
    id: params.id,
    name: params.name,
    description: params.description ?? "",
    dataJson: JSON.stringify(params.data ?? {}),
  });

  const response = await projectManagerClient.updateProject(request);
  if (!response.project) {
    throw new Error("updateProject response has no project");
  }

  return toProject(response.project);
}

/** 삭제 결과. `deleted=false` 면 스토리지에 파일이 남아 서버가 거절한 것이다. */
export interface DeleteProjectResult {
  deleted: boolean;
  remainingFiles: number;
  deletedFiles: number;
}

export async function deleteProject(id: string, force = false): Promise<DeleteProjectResult> {
  const response = await projectManagerClient.deleteProject(
    new DeleteProjectRequest({ id, force }),
  );
  return {
    deleted: response.deleted,
    remainingFiles: response.remainingFiles,
    deletedFiles: response.deletedFiles,
  };
}

export async function getProject(id: string): Promise<Project> {
  const response = await projectManagerClient.getProject(new GetProjectRequest({ id }));
  if (!response.project) {
    throw new Error("getProject response has no project");
  }
  return toProject(response.project);
}

/** 사용자의 프로젝트 목록 (updated_at 내림차순 — 서버 정렬). 본문 없는 요약이다 — 본문은 getProject 로 */
export async function listProjects(userId: string): Promise<ProjectSummary[]> {
  return readAllPages(async (pageToken) => {
    const response = await projectManagerClient.listProjects(
      new ListProjectsRequest({ userId, pageSize: LIST_PAGE_SIZE, pageToken }),
    );
    return { entries: (response.projects || []).map(toProjectSummary), nextPageToken: response.nextPageToken || '' };
  });
}

/** 페이지 단위 조회(프로젝트) — 호출자가 nextPageToken으로 이어 읽는다 */
export async function listSimulationHistoriesByProjectPage(
  projectId: string,
  pageSize = 50,
  pageToken = '',
): Promise<{ entries: SimulationEntry[]; nextPageToken: string }> {
  const normalized = projectId.trim();
  if (!normalized) return { entries: [], nextPageToken: '' };
  const response = await projectManagerClient.listSimulationHistoriesByProject(
    new ListSimulationHistoriesByProjectRequest({ projectId: normalized, pageSize, pageToken }),
  );
  const entries = (response.simulationHistories || []).flatMap((history) => {
    try {
      return [toSimulationEntry(history)];
    } catch (error) {
      console.warn('[projectManagerService] Failed to map simulation history row', history, error);
      return [];
    }
  });
  return { entries, nextPageToken: response.nextPageToken ?? '' };
}

/** 프로젝트의 실행 기록 전부(최근순) — 프로젝트 홈·모델 홈·분석 기록 패널·재시작 원본·퀵 실행 이력 */
export async function listSimulationHistoriesByProject(projectId: string): Promise<SimulationEntry[]> {
  return readAllPages((pageToken) => listSimulationHistoriesByProjectPage(projectId, LIST_PAGE_SIZE, pageToken));
}

/** 계정의 실행 기록 전부(최근순) — 실행 목록(#293). BFF 가 기록의 소유자로 범위를 건다 */
export async function listSimulationHistoriesByUser(userId: string): Promise<SimulationEntry[]> {
  return readAllPages((pageToken) => listSimulationHistoriesByUserPage(userId, LIST_PAGE_SIZE, pageToken));
}

/** 페이지 단위 조회 — 호출자가 nextPageToken으로 이어 읽는다. 클라이언트
 *  필터링(예: 빌트인 운전 실행 숨김)이 한 페이지를 소진해도 뒤 페이지의
 *  일반 이력을 마저 채울 수 있게 한다. */
export async function listSimulationHistoriesByUserPage(
  userId: string,
  pageSize = 50,
  pageToken = '',
): Promise<{ entries: SimulationEntry[]; nextPageToken: string }> {
  const normalized = userId.trim();
  if (!normalized) return { entries: [], nextPageToken: '' };
  const response = await projectManagerClient.listSimulationHistoriesByUser(
    new ListSimulationHistoriesByUserRequest({ userId: normalized, pageSize, pageToken }),
  );
  const entries = (response.simulationHistories || []).flatMap((history) => {
    try {
      return [toSimulationEntry(history)];
    } catch (error) {
      console.warn('[projectManagerService] Failed to map simulation history row', history, error);
      return [];
    }
  });
  return { entries, nextPageToken: response.nextPageToken ?? '' };
}

/** 이력 단건 조회 — 분석 뷰가 퀵런 이력의 taskIds(태스크 수)를 해석할 때 사용 */
export async function getSimulationHistory(simulationId: string): Promise<SimulationEntry | null> {
  const normalized = simulationId.trim();
  if (!normalized) return null;
  try {
    const response = await projectManagerClient.getSimulationHistory(
      new GetSimulationHistoryRequest({ simulationId: normalized }),
    );
    return response.simulationHistory ? toSimulationEntry(response.simulationHistory) : null;
  } catch (error) {
    console.warn('[projectManagerService] getSimulationHistory failed', simulationId, error);
    return null;
  }
}

/** 프로젝트 단건 조회(엄격) — **없음(NotFound)만 null**, 인증·연결·서버 실패는 던진다.
 *  "지워진 프로젝트" 판정의 권위 — 장애를 '없음'으로 오인하지 않는다(getSimulationHistoryStrict 와 같은 규칙). */
export async function getProjectStrict(id: string): Promise<Project | null> {
  const normalized = id.trim();
  if (!normalized) return null;
  try {
    return await getProject(normalized);
  } catch (error) {
    if (isNotFoundConnectError(error)) return null;
    throw error;
  }
}

/** 이력 단건 조회(엄격) — **없음(NotFound)만 null**, 인증·연결·서버 실패는 던진다.
 *  분석 자동 로드가 장애를 '없음'으로 오인해 단일 태스크를 지어내지 않게(리뷰 #388 R8). */
export async function getSimulationHistoryStrict(simulationId: string): Promise<SimulationEntry | null> {
  const normalized = simulationId.trim();
  if (!normalized) return null;
  try {
    const response = await projectManagerClient.getSimulationHistory(
      new GetSimulationHistoryRequest({ simulationId: normalized }),
    );
    return response.simulationHistory ? toSimulationEntry(response.simulationHistory) : null;
  } catch (error) {
    if (isNotFoundConnectError(error)) return null;
    throw error;
  }
}

/** 완료 실행 삭제 — 이력 행과 결과 산출물(MinIO)이 함께 지워진다(bff#64).
 *  서버가 소유·터미널 가드를 강제한다(실행 중은 FAILED_PRECONDITION). */
export async function deleteSimulationHistory(simulationId: string): Promise<void> {
  const normalized = simulationId.trim();
  if (!normalized) {
    throw new Error('deleteSimulationHistory: simulationId is required');
  }
  await projectManagerClient.deleteSimulationHistory(
    new DeleteSimulationHistoryRequest({ simulationId: normalized }),
  );
}

// ============================================
// workspaces — 프로젝트 상위 그룹
// ============================================

/** 프로젝트 소속 워크스페이스 변경. workspaceId 빈 값 = 그 프로젝트 소유자의 기본 워크스페이스(행)로(서버가 푼다) */
export async function setProjectWorkspace(projectId: string, workspaceId: string): Promise<Project> {
  const response = await projectManagerClient.setProjectWorkspace(
    new SetProjectWorkspaceRequest({ projectId, workspaceId }),
  );
  if (!response.project) {
    throw new Error("setProjectWorkspace response has no project");
  }
  return toProject(response.project);
}

export async function createWorkspace(params: {
  userId: string;
  name: string;
  description?: string;
}): Promise<Workspace> {
  const response = await projectManagerClient.createWorkspace(
    new CreateWorkspaceRequest({
      userId: params.userId,
      name: params.name,
      description: params.description ?? "",
    }),
  );
  if (!response.workspace) {
    throw new Error("createWorkspace response has no workspace");
  }
  return toWorkspace(response.workspace);
}

/** 사용자의 워크스페이스 목록 (created_at 오름차순 — 서버 정렬) */
export async function listWorkspaces(userId: string): Promise<Workspace[]> {
  const response = await projectManagerClient.listWorkspaces(
    new ListWorkspacesRequest({ userId }),
  );
  return (response.workspaces || []).map(toWorkspace);
}

export async function updateWorkspace(params: {
  id: string;
  userId: string;
  name: string;
  description?: string;
}): Promise<Workspace> {
  const response = await projectManagerClient.updateWorkspace(
    new UpdateWorkspaceRequest({
      id: params.id,
      userId: params.userId,
      name: params.name,
      description: params.description ?? "",
    }),
  );
  if (!response.workspace) {
    throw new Error("updateWorkspace response has no workspace");
  }
  return toWorkspace(response.workspace);
}

export async function deleteWorkspace(id: string, userId: string): Promise<void> {
  await projectManagerClient.deleteWorkspace(new DeleteWorkspaceRequest({ id, userId }));
}

/** 대상이 서버에 없다 — 뜻의 정본(`transportMeaning` 'gone') */
export function isNotFoundConnectError(error: unknown): boolean {
  return transportMeaningOf(error) === 'gone';
}

export const projectManagerService = {
  createProject,
  updateProject,
  deleteProject,
  getProject,
  listProjects,
  setProjectWorkspace,
  createWorkspace,
  listWorkspaces,
  updateWorkspace,
  deleteWorkspace,
  listSimulationHistoriesByProject,
  getSimulationHistory,
  isNotFoundConnectError,
};
